import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { CalendarRange, Download, Globe, MessageCircle, Plus, SlidersHorizontal, Sparkles, Timer } from 'lucide-react';
import { addDays, alignBack, isDayScheduled, streaks, type TodayItem } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { evaluate, liveHabits, todayFor } from '../domain/selectors';
import { deviceTimeZone, setTaskDone, updateSettings } from '../domain/actions';
import { currentVersion, currentWeekStartOf, toEngineSettings, type HabitRec } from '../domain/model';
import { getMeta, setMeta } from '../db/db';
import { isSyncEnabled } from '../db/repo';
import { Empty } from '../ui/Empty';
import { ProgressRing } from '../ui/ProgressRing';
import { Sheet } from '../ui/Sheet';
import { Segmented, Switch } from '../ui/Field';
import { HabitCard } from './HabitCard';
import { AgendaView, TimeRange } from './AgendaView';
import { buildAgenda, SLOTS } from '../domain/agenda';
import { consistency } from '../domain/progress';
import { TaskList } from '../ui/TaskList';
import { useToast } from '../ui/Toast';
import { InstallBanner } from './InstallBanner';
import { SyncBadge } from '../sync/SyncBadge';

interface Filters {
  hideCompleted: boolean;
  hideSkipped: boolean;
  hideFailed: boolean;
}

export function TodayScreen() {
  const { t, m, fmtDate } = useI18n();
  const { data, ctx, idx } = useApp();
  const toast = useToast();
  const [filters, setFilters] = useState<Filters>({ hideCompleted: false, hideSkipped: false, hideFailed: false });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [view, setView] = useState<'list' | 'agenda'>('list');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const requested = params.get('view');

  useEffect(() => {
    void getMeta<Filters>('todayFilters', filters).then(setFilters);
    // ?view=agenda (desde el chat o un acceso directo) gana a la preferencia guardada y la actualiza.
    const wanted: 'list' | 'agenda' | null = requested === 'agenda' || requested === 'list' ? requested : null;
    if (wanted) void setMeta('todayView', wanted).then(() => navigate('/', { replace: true }));
    void Promise.resolve(wanted ?? getMeta<'list' | 'agenda'>('todayView', 'list')).then(setView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);
  const updateFilters = (f: Filters) => {
    setFilters(f);
    void setMeta('todayFilters', f);
  };

  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const habits = useMemo(() => liveHabits(data.habits), [data.habits]);

  const rows = useMemo(() => {
    const out: { habit: HabitRec; item: TodayItem; streak: { current: number; unit: 'day' | 'week' | 'month' } | null }[] = [];
    for (const h of habits) {
      const item = todayFor(h, idx, data.vacations, settings, ctx.today);
      if (!item) continue;
      const st = h.kind === 'task' ? null : streaks(evaluate(h, idx, data.vacations, settings, ctx.today));
      out.push({ habit: h, item, streak: st });
    }
    return out;
  }, [habits, idx, data.vacations, settings, ctx.today]);

  const statusOf = (r: (typeof rows)[number]) => r.item.task ?? r.item.ue?.status ?? 'pending';
  // Primero lo pendiente; lo resuelto (hecho, omitido, vacaciones) al final de su sección.
  const resolved = (r: (typeof rows)[number]) => ['completed', 'skipped', 'vacation'].includes(statusOf(r));
  const visible = [...rows].sort((a, b) => Number(resolved(a)) - Number(resolved(b))).filter((r) => {
    const s = statusOf(r);
    if (filters.hideCompleted && s === 'completed') return false;
    if (filters.hideSkipped && (s === 'skipped' || s === 'vacation')) return false;
    if (filters.hideFailed && (s === 'missed' || s === 'failed')) return false;
    return true;
  });
  // FR-HAB-14: el hábito excluido se sigue viendo y registrando, pero no entra en el porcentaje.
  const countable = rows.filter((r) => !r.habit.excludedFromProgress && !['skipped', 'vacation'].includes(statusOf(r)));
  const done = countable.filter((r) => statusOf(r) === 'completed' || (r.habit.kind === 'quit' && statusOf(r) === 'pending')).length;

  const groupsById = new Map(data.groups.filter((g) => !g.deletedAt).map((g) => [g.id, g]));
  const sections: { key: string; title: string; rows: typeof visible }[] = [];
  const sortedGroups = [...groupsById.values()].sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));
  for (const g of sortedGroups) {
    const rs = visible.filter((r) => r.habit.kind !== 'task' && r.habit.primaryGroupId === g.id);
    if (rs.length) sections.push({ key: g.id, title: g.name, rows: rs });
  }
  const ungrouped = visible.filter((r) => r.habit.kind !== 'task' && (!r.habit.primaryGroupId || !groupsById.has(r.habit.primaryGroupId)));
  if (ungrouped.length) sections.push({ key: 'none', title: sections.length ? t('today.ungrouped') : '', rows: ungrouped });
  const tasks = visible.filter((r) => r.habit.kind === 'task');
  if (tasks.length) sections.push({ key: 'tasks', title: t('today.tasks'), rows: tasks });

  // La agenda también muestra las fechas importantes del Cerebro: con ellas hay agenda aunque no haya hábitos.
  const hasAgenda = habits.length > 0 || data.notes.some((n) => n.kind === 'date' && !n.deletedAt);
  const weekday = fmtDate(ctx.today, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <header className="page-header">
        <div>
          <p className="page-header__greeting">{t(`today.greeting.${dayPart(ctx.tz)}`)}</p>
          <h1>{t('today.title')}</h1>
          <p className="page-header__sub page-header__date">{weekday}</p>
        </div>
        <div className="row">
          <SyncBadge />
          <Link to="/focus" className="icon-btn" aria-label={t('focus.open')}>
            <Timer size={22} aria-hidden="true" />
          </Link>
          <button type="button" className="icon-btn" onClick={() => setFiltersOpen(true)} aria-label={t('today.showFilters')}>
            <SlidersHorizontal size={22} aria-hidden="true" />
          </button>
          <Link to="/habits/new" className="btn btn--primary" aria-label={t('editor.newTitle')}>
            <Plus size={20} aria-hidden="true" />
            <span className="hide-sm">{t('common.add')}</span>
          </Link>
        </div>
      </header>

      {view === 'list' && habits.length > 0 && <DayStrip habits={habits} />}
      <TimeZoneBanner />
      <InstallBanner />
      <BackupReminder habitsCount={habits.length} />

      {!hasAgenda ? (
        <Welcome />
      ) : (
        <>
          {rows.length > 0 && <DayHero done={done} total={countable.length} tagline={m.app.tagline} />}
          <AskChat />
          {view === 'list' && <UpNext />}
          <Segmented
            legend={t('agenda.view')}
            value={view}
            onChange={(v) => {
              setView(v);
              void setMeta('todayView', v);
            }}
            options={[
              { value: 'list', label: t('agenda.list') },
              { value: 'agenda', label: t('agenda.agenda') },
            ]}
          />
        </>
      )}

      {!hasAgenda ? null : view === 'agenda' ? (
        <AgendaView />
      ) : habits.length === 0 ? (
        <Welcome />
      ) : rows.length === 0 ? (
        <Empty title={t('today.nothingTodayTitle')} body={t('today.nothingTodayBody')} />
      ) : (
        <>
          <div className="stack-l">
            {sections.map((s) => (
              <section key={s.key} className="stack" aria-label={s.title || t('today.title')}>
                {s.title && <h2 className="section-title">{s.title}</h2>}
                {s.key === 'tasks' ? (
                  <TaskList
                    labels={{ complete: t('habit.actions.complete'), uncomplete: t('habit.actions.uncomplete') }}
                    items={s.rows.map((r) => ({
                      id: r.habit.id,
                      title: r.habit.name,
                      done: r.item.task === 'completed',
                      overdue: r.item.task === 'overdue',
                      meta: r.habit.dueDate
                        ? `${r.item.task === 'overdue' ? `${t('habit.status.overdue')} · ` : ''}${fmtDate(r.habit.dueDate)}`
                        : undefined,
                      href: `/habits/${r.habit.id}`,
                    }))}
                    onToggle={(id, done) => {
                      const h = s.rows.find((r) => r.habit.id === id)?.habit;
                      if (h) setTaskDone(h, done).catch(() => toast.show(t('errors.save')));
                    }}
                  />
                ) : (
                  s.rows.map((r) => <HabitCard key={r.habit.id} habit={r.habit} item={r.item} streak={r.streak} />)
                )}
              </section>
            ))}
            {visible.length < rows.length && <p className="muted small">{t('today.hiddenCount', { count: rows.length - visible.length })}</p>}
          </div>
        </>
      )}

      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('today.showFilters')}>
        <Switch label={t('today.hideCompleted')} checked={filters.hideCompleted} onChange={(v) => updateFilters({ ...filters, hideCompleted: v })} />
        <Switch label={t('today.hideSkipped')} checked={filters.hideSkipped} onChange={(v) => updateFilters({ ...filters, hideSkipped: v })} />
        <Switch label={t('today.hideFailed')} checked={filters.hideFailed} onChange={(v) => updateFilters({ ...filters, hideFailed: v })} />
      </Sheet>
    </>
  );
}

/** Parte del día en la zona horaria del usuario, para el saludo. */
function dayPart(tz: string): 'morning' | 'afternoon' | 'evening' {
  const h = Number(new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', hourCycle: 'h23' }).format(new Date()));
  return h >= 5 && h < 13 ? 'morning' : h >= 13 && h < 20 ? 'afternoon' : 'evening';
}

/** Primeros pasos sugeridos (sin datos todavía) y consultas útiles (con datos). Se abren en el chat como borrador. */
const STARTER_SUGGESTIONS = ['today.suggest.read', 'today.suggest.routine', 'today.suggest.water'] as const;
const DAILY_SUGGESTIONS = ['today.suggest.today', 'today.suggest.week', 'today.suggest.neglected'] as const;

function ChatSuggestions({ keys }: { keys: readonly string[] }) {
  const { t } = useI18n();
  return (
    <div className="ask__chips" role="group" aria-label={t('today.suggestionsLabel')}>
      {keys.map((k) => (
        <Link key={k} to={`/chat?draft=${encodeURIComponent(t(k))}`} className="chip-toggle ask__chip">
          {t(k)}
        </Link>
      ))}
    </div>
  );
}

/** Bienvenida sin hábitos: el chat como acción principal, crear un hábito como alternativa y sugerencias. */
function Welcome() {
  const { t } = useI18n();
  return (
    <Empty
      title={t('today.emptyTitle')}
      body={t('today.emptyBody')}
      action={
        <div className="stack" style={{ alignItems: 'center' }}>
          <div className="empty__actions">
            <Link to="/chat" className="btn btn--primary">
              <MessageCircle size={20} aria-hidden="true" /> {t('today.planWithChat')}
            </Link>
            <Link to="/habits/new" className="btn btn--outline">
              <Plus size={20} aria-hidden="true" /> {t('today.emptyCta')}
            </Link>
          </div>
          <p className="ask__hint">{t('today.tryWith')}</p>
          <ChatSuggestions keys={STARTER_SUGGESTIONS} />
        </div>
      }
    />
  );
}

/**
 * Tira de la semana: solo visual (el día de hoy ya está en el encabezado). El punto marca
 * los días con algún hábito programado.
 */
function DayStrip({ habits }: { habits: HabitRec[] }) {
  const { fmtDate } = useI18n();
  const { ctx } = useApp();
  const start = alignBack(ctx.today, currentWeekStartOf(ctx.settings));
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  // shortcut: usa la versión vigente de cada hábito y no mira traslados ni cuotas; pasar al motor si la tira deja de ser decorativa.
  const scheduled = (d: string) =>
    habits.some((h) => h.kind !== 'task' && !h.archivedAt && h.startDate <= d && isDayScheduled(currentVersion(h).schedule, d, h.startDate));
  return (
    <ol className="days" aria-hidden="true">
      {days.map((d) => (
        <li key={d} className="days__day" data-today={d === ctx.today || undefined}>
          <span className="days__name">{fmtDate(d, { weekday: 'narrow' })}</span>
          <span className="days__num num">{Number(d.slice(8))}</span>
          <span className="days__dot" data-on={scheduled(d) || undefined} />
        </li>
      ))}
    </ol>
  );
}

/** Entrada al chat siempre visible en Hoy: parece un campo, pero abre la conversación. */
function AskChat() {
  const { t } = useI18n();
  return (
    <section className="ask" aria-labelledby="ask-title">
      <h2 id="ask-title" className="visually-hidden">
        {t('today.askTitle')}
      </h2>
      <Link to="/chat" className="ask__field">
        <Sparkles size={18} aria-hidden="true" />
        <span>{t('today.askPlaceholder')}</span>
        <MessageCircle size={18} aria-hidden="true" className="ask__icon" />
      </Link>
      <ChatSuggestions keys={DAILY_SUGGESTIONS} />
    </section>
  );
}

/**
 * Progreso del día con datos reales: hechos de hoy, constancia de los últimos 7 días cerrados
 * (hoy no cuenta porque sigue abierto) y acceso a planificar.
 */
function DayHero({ done, total, tagline }: { done: number; total: number; tagline: string }) {
  const { t } = useI18n();
  const { data, ctx, idx } = useApp();
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const c = useMemo(() => consistency(data.habits, idx, data.vacations, settings, ctx.today, 7), [data.habits, idx, data.vacations, settings, ctx.today]);
  const label = t('today.summary', { done, total });
  return (
    <section className="hero" aria-label={label}>
      <ProgressRing value={total ? done / total : 1} size={84} stroke={9} label={label} />
      <div className="hero__body">
        <div className="hero__title num">{done === total ? t('today.allDone') : label}</div>
        <div className="hero__meta small">
          {c.rate === null ? t('today.consistencyNone') : t('today.consistency', { pct: Math.round(c.rate * 100), done: c.done, total: c.total })}
        </div>
        <div className="hero__tagline xsmall">{tagline}</div>
      </div>
      <div className="hero__actions">
        <Link to="/?view=agenda" className="btn btn--outline btn--sm">
          <CalendarRange size={16} aria-hidden="true" /> {t('today.seeAgenda')}
        </Link>
      </div>
    </section>
  );
}

/** "¿Qué tengo después?": lo próximo con hora de hoy y las tareas vencidas, desde la agenda real. */
function UpNext() {
  const { t } = useI18n();
  const { data, ctx, idx } = useApp();
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const now = new Intl.DateTimeFormat('en-GB', { timeZone: ctx.tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date());
  const agenda = useMemo(
    () => buildAgenda({ habits: data.habits, reminders: data.reminders, notes: data.notes, vacations: data.vacations, idx, settings, day: ctx.today, today: ctx.today }),
    [data.habits, data.reminders, data.notes, data.vacations, idx, settings, ctx.today],
  );
  const entries = SLOTS.flatMap((s) => agenda.slots[s]);
  // Lo que está en curso (empezó y aún no terminó) también cuenta como "ahora".
  const next = entries.filter((e) => !e.done && e.time && (e.end ?? e.time) > now).slice(0, 3);
  const overdue = entries.filter((e) => e.overdue && !e.done).length;
  if (!next.length && !overdue) return null;
  return (
    <section className="upnext" aria-labelledby="upnext-title">
      <h2 id="upnext-title" className="section-title">
        {t('today.upNext')}
      </h2>
      {next.length ? (
        <ul className="upnext__list">
          {next.map((e) => (
            <li key={e.key} className="upnext__item">
              <TimeRange entry={e} />
              <span className="upnext__name">{e.title}</span>
              {e.time! <= now && <span className="badge badge--accent">{t('today.now')}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">{t('today.nothingTimed')}</p>
      )}
      {overdue > 0 && <p className="small upnext__overdue">{t('today.overdueTasks', { count: overdue })}</p>}
    </section>
  );
}

/** FR-TIME-06: nunca se cambia la zona horaria en silencio. */
function TimeZoneBanner() {
  const { t } = useI18n();
  const { ctx } = useApp();
  const device = deviceTimeZone();
  const s = ctx.settings;
  if (s.tzMode !== 'follow_device' || device === s.effectiveTz) return null;
  return (
    <div className="banner banner--info" role="region" aria-label={t('today.tzTitle')}>
      <Globe size={22} aria-hidden="true" />
      <div className="banner__body">
        <strong>{t('today.tzTitle')}</strong>
        <span className="small">{t('today.tzBody', { device, current: s.effectiveTz })}</span>
        <div className="row row--wrap">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => updateSettings({ effectiveTz: device, homeTz: device })}>
            {t('today.tzUseNew', { device })}
          </button>
          <button type="button" className="btn btn--sm" onClick={() => updateSettings({ tzMode: 'fixed' })}>
            {t('today.tzKeep', { current: s.effectiveTz })}
          </button>
        </div>
      </div>
    </div>
  );
}

/** FR-DATA-06: recordatorio periódico de exportar en modo sin cuenta. */
function BackupReminder({ habitsCount }: { habitsCount: number }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  useEffect(() => {
    void (async () => {
      if (habitsCount === 0 || (await isSyncEnabled())) return setShow(false);
      const last = await getMeta<number>('lastBackupNudge', 0);
      const firstUse = await getMeta<number>('firstUseAt', Date.now());
      const week = 7 * 86_400_000;
      setShow(Date.now() - firstUse > week && Date.now() - last > week * 2);
    })();
  }, [habitsCount]);
  if (!show) return null;
  return (
    <div className="banner">
      <Download size={22} aria-hidden="true" />
      <div className="banner__body">
        <span className="small">{t('today.backupReminder')}</span>
        <div className="row">
          <Link className="btn btn--sm" to="/settings#data" onClick={() => void setMeta('lastBackupNudge', Date.now())}>
            {t('today.backupAction')}
          </Link>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              void setMeta('lastBackupNudge', Date.now());
              setShow(false);
            }}
          >
            {t('today.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
